import {
  ArrowDownAZ,
  ArrowDownNarrowWide,
  ArrowDownUp,
  ArrowDownWideNarrow,
  ArrowDownZA,
  Check,
  ChevronDown,
} from "lucide-react";
import { Button } from "~/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import type { SortDirection, SortKind } from "~/hooks/useSort";

interface SortMenuProps {
  options: { key: string; label: string; kind: SortKind }[];
  sortKey: string;
  direction: SortDirection;
  activeLabel: string;
  /** Select a different field (hook resets direction to that field's default). */
  onSelectKey: (key: string) => void;
  /** Flip asc/desc on the already-active field. */
  onToggleDirection: () => void;
}

/** Down-pointing arrow whose content (A→Z, narrow→wide) shows the sort order. */
const directionIcon = (kind: SortKind, direction: SortDirection) => {
  if (kind === "alpha") {
    return direction === "asc" ? ArrowDownAZ : ArrowDownZA;
  }
  return direction === "asc" ? ArrowDownNarrowWide : ArrowDownWideNarrow;
};

/**
 * View-control sort dropdown: a labeled trigger showing the active field, with
 * a menu of fields. Selecting the active field again flips sort direction.
 */
export const SortMenu = ({
  options,
  sortKey,
  direction,
  activeLabel,
  onSelectKey,
  onToggleDirection,
}: SortMenuProps) => {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="min-w-36 justify-between text-xs">
          <span className="flex items-center gap-1.5">
            <ArrowDownUp className="size-4" aria-hidden="true" />
            {activeLabel}
          </span>
          <ChevronDown className="size-4 text-muted-foreground" aria-hidden="true" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-44">
        {options.map((option) => {
          const isActive = option.key === sortKey;
          const DirectionIcon = directionIcon(option.kind, direction);
          return (
            <DropdownMenuItem
              key={option.key}
              onSelect={(e) => {
                // Keep the menu open so direction flips are visible.
                e.preventDefault();
                if (isActive) {
                  onToggleDirection();
                } else {
                  onSelectKey(option.key);
                }
              }}
            >
              <Check className={isActive ? "size-4" : "size-4 opacity-0"} aria-hidden="true" />
              <span className="flex-1">{option.label}</span>
              {isActive && (
                <DirectionIcon className="size-4 text-muted-foreground" aria-hidden="true" />
              )}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
